import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AddressBook, AddressBookContext, AddressBookEntry, AddressBookEntryRequest } from './address-book.models';

const BASE_URL = '/proxy/api/v0/userservice';

@Injectable({ providedIn: 'root' })
export class AddressBookService {
  constructor(private readonly http: HttpClient) { }

  getAddressBooks(context: AddressBookContext = {}): Observable<AddressBook[]> {
    return this.http.get<AddressBook[]>(this.collectionUrl(context));
  }

  createAddressBook(name: string, context: AddressBookContext = {}): Observable<AddressBook> {
    return this.http.post<AddressBook>(this.collectionUrl(context), { name });
  }

  getPlatformAddressBooks(): Observable<AddressBook[]> {
    return this.http.get<AddressBook[]>(`${BASE_URL}/address-books/platform`);
  }

  getPlatformEntries(): Observable<AddressBookEntry[]> {
    return this.http.get<AddressBookEntry[]>(`${BASE_URL}/address-books/platform/entries`);
  }

  getAddressBook(addressBookId: number): Observable<AddressBook> {
    return this.http.get<AddressBook>(`${BASE_URL}/address-books/${addressBookId}`);
  }

  renameAddressBook(addressBookId: number, name: string): Observable<AddressBook> {
    return this.http.put<AddressBook>(`${BASE_URL}/address-books/${addressBookId}`, { name });
  }

  deleteAddressBook(addressBookId: number): Observable<void> {
    return this.http.delete<void>(`${BASE_URL}/address-books/${addressBookId}`);
  }

  getEntries(addressBookId: number): Observable<AddressBookEntry[]> {
    return this.http.get<AddressBookEntry[]>(`${BASE_URL}/address-books/${addressBookId}/entries`);
  }

  getEntry(addressBookId: number, entryId: number): Observable<AddressBookEntry> {
    return this.http.get<AddressBookEntry>(`${BASE_URL}/address-books/${addressBookId}/entries/${entryId}`);
  }

  createEntry(addressBookId: number, request: AddressBookEntryRequest): Observable<AddressBookEntry> {
    return this.http.post<AddressBookEntry>(`${BASE_URL}/address-books/${addressBookId}/entries`, request);
  }

  updateEntry(addressBookId: number, entryId: number,
      request: AddressBookEntryRequest): Observable<AddressBookEntry> {
    return this.http.put<AddressBookEntry>(
      `${BASE_URL}/address-books/${addressBookId}/entries/${entryId}`, request);
  }

  deleteEntry(addressBookId: number, entryId: number): Observable<void> {
    return this.http.delete<void>(`${BASE_URL}/address-books/${addressBookId}/entries/${entryId}`);
  }

  fullTextSearch(addressBookId: number, query: string): Observable<AddressBookEntry[]> {
    return this.http.get<AddressBookEntry[]>(`${BASE_URL}/address-books/${addressBookId}/entries/search`, {
      params: new HttpParams().set('query', query)
    });
  }

  prefixSearch(addressBookId: number, query: string): Observable<AddressBookEntry[]> {
    return this.http.get<AddressBookEntry[]>(`${BASE_URL}/address-books/${addressBookId}/entries/search/prefix`, {
      params: new HttpParams().set('query', query)
    });
  }

  private collectionUrl(context: AddressBookContext): string {
    return context.organisationId
      ? `${BASE_URL}/organisations/${context.organisationId}/address-books`
      : `${BASE_URL}/address-books`;
  }
}
